import { Dispatch } from 'react'
import { AppState } from '../../../../global/store'
import { ApiResponse, RequestState } from '../../../types'
import * as certificatesApi from '../services/api'
import { Certificate } from './store'
import ReactGA from 'react-ga'

import {
  LOAD_ACCOUNT_CERTIFICATES_REQUEST,
  LOAD_ACCOUNT_CERTIFICATES_RECEIVED,
  LOAD_ACCOUNT_CERTIFICATES_ERROR,
  LOAD_AVAILABLE_CERTIFICATES_REQUEST,
  LOAD_AVAILABLE_CERTIFICATES_RECEIVED,
  LOAD_AVAILABLE_CERTIFICATES_ERROR,
  UPDATE_CERT_FORM_FIELD,
  REMOVE_ACCOUNT_CERTIFICATE_REQUEST,
  REMOVE_ACCOUNT_CERTIFICATE_RECEIVED,
  REMOVE_ACCOUNT_CERTIFICATE_ERROR
} from './reducers'

export type CertficateSettingsAction =
  | { type: typeof LOAD_ACCOUNT_CERTIFICATES_REQUEST }
  | { type: typeof LOAD_ACCOUNT_CERTIFICATES_RECEIVED, certificates: Certificate[] }
  | { type: typeof LOAD_ACCOUNT_CERTIFICATES_ERROR }
  | { type: typeof LOAD_AVAILABLE_CERTIFICATES_REQUEST }
  | { type: typeof LOAD_AVAILABLE_CERTIFICATES_RECEIVED, certificates: Certificate[] }
  | { type: typeof LOAD_AVAILABLE_CERTIFICATES_ERROR }
  | { type: typeof UPDATE_CERT_FORM_FIELD, key: string, value: string }
  | { type: typeof REMOVE_ACCOUNT_CERTIFICATE_REQUEST, certificateId: string }
  | { type: typeof REMOVE_ACCOUNT_CERTIFICATE_RECEIVED, certificateId: string }
  | { type: typeof REMOVE_ACCOUNT_CERTIFICATE_ERROR, certificateId: string }

export function loadAvailableCertificates(accountId: string) {
  return async function (_state: AppState, dispatch: Dispatch<CertficateSettingsAction>) {
    try {
      dispatch({
        type: LOAD_AVAILABLE_CERTIFICATES_REQUEST
      })

      const result = await certificatesApi.getAvailableCertificates(accountId)
      const mappedCertificates = result.data.map((data: ApiResponse) => mapCertificate(data))

      dispatch({
        type: LOAD_AVAILABLE_CERTIFICATES_RECEIVED,
        certificates: mappedCertificates
      })
    } catch {
      dispatch({
        type: LOAD_AVAILABLE_CERTIFICATES_ERROR
      })
    }
  }
}

export function loadAccountCertificates(accountId: string) {
  return async function (state: AppState, dispatch: Dispatch<CertficateSettingsAction>) {
    try {
      dispatch({
        type: LOAD_ACCOUNT_CERTIFICATES_REQUEST
      })

      const result = await certificatesApi.getAccountCertificates(accountId)
      const mappedCertificates = result.data.map((data: ApiResponse) => mapAccountCertificate(data))
        .sort((a: Certificate, b: Certificate) => a.label.localeCompare(b.label))

      dispatch({
        type: LOAD_ACCOUNT_CERTIFICATES_RECEIVED,
        certificates: mappedCertificates
      })
    } catch {
      dispatch({
        type: LOAD_ACCOUNT_CERTIFICATES_ERROR
      })
    }
  }
}

export function updateCertFormField(key: string, value: string) {
  return async function (_state: AppState, dispatch: Dispatch<CertficateSettingsAction>) {    
    dispatch({
      type: UPDATE_CERT_FORM_FIELD,
      key,
      value
    })    
  }
}

export function addAccountCertificate(accountId: string, certificateId: string, label: string) {
  return async function () {
    await certificatesApi.addAccountCertificate(accountId, certificateId, label)
    ReactGA.event({ category: 'account-settings', action: 'Certificate Added' })
  }
}

export function removeAccountCertificate(accountId: string, certificateId: string) {
  return async function (_state: AppState, dispatch: Dispatch<CertficateSettingsAction>) {
    ReactGA.event({ category: 'account-settings', action: 'Certificate Removed' })

    try {
      dispatch({
        type: REMOVE_ACCOUNT_CERTIFICATE_REQUEST,
        certificateId
      })

      await certificatesApi.removeAccountCertificate(accountId, certificateId)
      ReactGA.event({ category: 'account-settings', action: 'Certificate Removed' })

      dispatch({
        type: REMOVE_ACCOUNT_CERTIFICATE_RECEIVED,
        certificateId
      })
    } catch {
      dispatch({
        type: REMOVE_ACCOUNT_CERTIFICATE_ERROR,
        certificateId
      })
    }
  }
}

function mapAccountCertificate(data: ApiResponse) {
  return {
    id: data.id,
    key: data.key,
    label: data.label,
    active: data.active,
    createdAt: data.created_at,
    deactivatedAt: data.deactivated_at,
    deleteRequestState: RequestState.NotLoaded
  }
}

function mapCertificate(data: ApiResponse) {
  return {
    id: data.id,
    key: data.key,
    label: data.label
  }
}
